/**
 * Dashboard service for shared collection views (read-only, viewer context)
 * Uses direct table queries so RLS enforces share access automatically.
 */

import { supabase } from './supabase';

export const sharedDashboardService = {
  async getDashboardStats(userId) {
    const [bottlesResult, distilleriesResult] = await Promise.all([
      supabase.from('bottles').select('status, purchase_price').eq('user_id', userId),
      supabase.from('distilleries').select('id', { count: 'exact', head: true }).eq('user_id', userId)
    ]);

    if (bottlesResult.error) throw bottlesResult.error;
    if (distilleriesResult.error) throw distilleriesResult.error;

    const bottles = bottlesResult.data || [];
    return {
      total_bottles: bottles.length,
      total_distilleries: distilleriesResult.count || 0,
      total_value: bottles.reduce((s, b) => s + (b.purchase_price || 0), 0),
      unopened_count: bottles.filter(b => b.status === 'Unopened').length,
      opened_count: bottles.filter(b => b.status === 'Opened').length,
      empty_count: bottles.filter(b => b.status === 'Empty').length
    };
  },

  async getBottlesByType(userId) {
    const { data, error } = await supabase
      .from('bottles')
      .select('type')
      .eq('user_id', userId);

    if (error) throw error;
    const counts = {};
    (data || []).forEach(b => { counts[b.type] = (counts[b.type] || 0) + 1; });
    return Object.entries(counts)
      .map(([type, count]) => ({ type, count }))
      .sort((a, b) => b.count - a.count);
  },

  async getBottlesByCountry(userId) {
    const { data, error } = await supabase
      .from('bottles')
      .select('distilleries(country)')
      .eq('user_id', userId)
      .not('distillery_id', 'is', null);

    if (error) throw error;
    const counts = {};
    (data || []).forEach(b => {
      const c = b.distilleries?.country;
      if (c) counts[c] = (counts[c] || 0) + 1;
    });
    return Object.entries(counts)
      .map(([country, count]) => ({ country, count }))
      .sort((a, b) => b.count - a.count);
  },

  async getRecentBottles(userId, limit = 5) {
    const { data, error } = await supabase
      .from('bottles')
      .select('id, bottle_name, type, status, purchase_date, distilleries(distillery_name)')
      .eq('user_id', userId)
      .order('purchase_date', { ascending: false, nullsFirst: false })
      .order('bottle_name', { ascending: true })
      .limit(limit);

    if (error) throw error;
    return data || [];
  }
};
